import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuPortal } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format, isToday, isTomorrow, parseISO } from 'date-fns';
import LanguageSelector from '../components/LanguageSelector';
import { 
  ListTodo, Plus, Lock, Users, Calendar as CalendarIcon, 
  Clock, Check, Loader2, Trash2, X, ChevronRight, 
  Circle, CheckCircle2, Layers, GripVertical, 
  Sun, AlertTriangle, Star, ArrowUpRight, Users2, Share2,
  Paperclip, FileText, Download, Upload, Pencil, Copy,
  MoreHorizontal, ArrowRightLeft, Search
} from 'lucide-react';
import GuestSignupBanner from '../components/GuestSignupBanner';
import GuestActionModal from '../components/GuestActionModal';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SortableTask from '../components/todo/SortableTask';

// Helper to get hold slot label
const getHoldSlotLabel = (slot) => {
  switch (slot) {
    case 1: return 'Primary';
    case 2: return 'Secondary';
    case 3: return 'Tertiary';
    default: return '';
  }
};

// Helper to get hold slot color
const getHoldSlotColor = (slot) => {
  switch (slot) {
    case 1: return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    case 2: return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 3: return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TodoLists = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, isGuestMode, getAuthHeaders } = useAuth();
  
  // State
  const [loading, setLoading] = useState(true);
  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState(null);
  const [selectedList, setSelectedList] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [activeView, setActiveView] = useState('today'); // 'today', 'holds', 'events', 'private'
  
  // Modals
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
  const [showEditListModal, setShowEditListModal] = useState(false);
  const [editingListName, setEditingListName] = useState('');
  const [editingListDesc, setEditingListDesc] = useState('');
  const [attachmentTask, setAttachmentTask] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [attachmentCounts, setAttachmentCounts] = useState({});
  const [uploadingFile, setUploadingFile] = useState(false);
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [docInfo, setDocInfo] = useState({ totalCount: 0, uploadLimit: 5, remainingSlots: 5 });
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docInputRef = React.useRef(null);

  const [createListData, setCreateListData] = useState({
    name: '',
    sourceType: 'private',
    description: '',
    eventId: null,
    holdSlot: null,
    publicEventId: null
  });
  const [createTaskData, setCreateTaskData] = useState({
    title: '',
    description: '',
    dueAt: null,
    priority: 'normal',
    assigneeId: null
  });
  const [saving, setSaving] = useState(false);
  const [connections, setConnections] = useState([]); // For task assignment
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [taskSearchQuery, setTaskSearchQuery] = useState('');

  // Circles and groups for linking
  const [circles, setCircles] = useState([]);
  const [groups, setGroups] = useState([]);
  
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch all lists
  const fetchLists = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/todolists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLists(response.data);
    } catch (error) {
      console.error('Failed to fetch lists:', error);
      toast.error('Failed to load to-do lists');
    }
  }, [token]);

  // Fetch tasks for selected list
  const fetchTasks = useCallback(async (listId) => {
    try {
      const response = await axios.get(`${API}/todolists/${listId}/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast.error('Failed to load tasks');
      return [];
    }
  }, [token]);

  // Fetch today's tasks
  const fetchTodayTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tasks/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodayTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch today tasks:', error);
    }
  }, [token]);

  // Fetch connections for task assignment
  const fetchConnections = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/relationships`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Only show registered users for assignment
      setConnections(response.data.filter(c => c.isRegistered));
    } catch (error) {
      console.error('Failed to fetch connections:', error);
    }
  }, [token]);

  // Fetch attachment counts for all tasks in a list
  const fetchAttachmentCounts = useCallback(async (taskList) => {
    const counts = {};
    await Promise.all(
      taskList.map(async (task) => {
        try {
          const response = await axios.get(
            `${API}/todolists/${task.listId}/tasks/${task.id}/attachments`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.data.length > 0) {
            counts[task.id] = response.data.length;
          }
        } catch {
          // ignore
        }
      })
    );
    setAttachmentCounts(prev => ({ ...prev, ...counts }));
  }, [token]);

  // Open attachments panel for a task
  const handleOpenAttachments = useCallback(async (task) => {
    setAttachmentTask(task);
    setShowAttachmentsModal(true);
    setLoadingAttachments(true);
    try {
      const response = await axios.get(
        `${API}/todolists/${task.listId}/tasks/${task.id}/attachments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAttachments(response.data);
    } catch (error) {
      console.error('Failed to fetch attachments:', error);
      toast.error('Failed to load attachments');
    } finally {
      setLoadingAttachments(false);
    }
  }, [token]);

  // Upload PDF attachment
  const handleUploadAttachment = useCallback(async (file) => {
    if (!attachmentTask) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10 MB.');
      return;
    }
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(
        `${API}/todolists/${attachmentTask.listId}/tasks/${attachmentTask.id}/attachments`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setAttachments(prev => [response.data, ...prev]);
      setAttachmentCounts(prev => ({
        ...prev,
        [attachmentTask.id]: (prev[attachmentTask.id] || 0) + 1
      }));
      toast.success('PDF uploaded');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingFile(false);
    }
  }, [attachmentTask, token]);

  // Download attachment
  const handleDownloadAttachment = useCallback(async (att) => {
    if (!attachmentTask) return;
    try {
      const response = await axios.get(
        `${API}/todolists/${attachmentTask.listId}/tasks/${attachmentTask.id}/attachments/${att.id}/download`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = att.originalFilename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download failed');
    }
  }, [attachmentTask, token]);

  // Delete attachment
  const handleDeleteAttachment = useCallback(async (att) => {
    if (!attachmentTask) return;
    try {
      await axios.delete(
        `${API}/todolists/${attachmentTask.listId}/tasks/${attachmentTask.id}/attachments/${att.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAttachments(prev => prev.filter(a => a.id !== att.id));
      setAttachmentCounts(prev => ({
        ...prev,
        [attachmentTask.id]: Math.max(0, (prev[attachmentTask.id] || 1) - 1)
      }));
      toast.success('Attachment deleted');
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete attachment');
    }
  }, [attachmentTask, token]);

  // Initial load
  const fetchDocuments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/documents`, { headers: getAuthHeaders() });
      setDocuments(res.data.documents || []);
      setDocInfo({
        totalCount: res.data.totalCount || 0,
        uploadLimit: res.data.uploadLimit || 5,
        remainingSlots: res.data.remainingSlots || 0
      });
    } catch { /* ignore */ }
  }, [getAuthHeaders]);

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/documents/upload`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded!');
      fetchDocuments();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.error === 'upload_limit_reached') {
        toast.error(detail.message);
      } else {
        toast.error('Upload failed');
      }
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const handleDocDelete = async (docId) => {
    try {
      await axios.delete(`${API}/documents/${docId}`, { headers: getAuthHeaders() });
      toast.success('Document deleted');
      fetchDocuments();
    } catch { toast.error('Failed to delete document'); }
  };

  useEffect(() => {
    if (isGuestMode) {
      setLoading(false);
      return;
    }
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLists(), fetchTodayTasks(), fetchConnections(), fetchDocuments()]);
      setLoading(false);
    };
    loadData();
  }, [fetchLists, fetchTodayTasks, fetchConnections, fetchDocuments, isGuestMode]);

  // Handle list selection
  useEffect(() => {
    if (selectedListId) {
      const list = lists.find(l => l.id === selectedListId);
      setSelectedList(list);
      fetchTasks(selectedListId).then((taskData) => {
        if (taskData && taskData.length > 0) {
          fetchAttachmentCounts(taskData);
        }
      });
      setActiveView(null);
    }
  }, [selectedListId, lists, fetchTasks, fetchAttachmentCounts]);

  // Handle URL params for creating list from hold
  useEffect(() => {
    const action = searchParams.get('action');
    const eventId = searchParams.get('eventId');
    const holdSlot = searchParams.get('holdSlot');
    
    if (action === 'createList' && eventId && holdSlot) {
      setCreateListData(prev => ({
        ...prev,
        sourceType: 'hold',
        eventId,
        holdSlot: parseInt(holdSlot),
        name: `${getHoldSlotLabel(parseInt(holdSlot))} Hold Tasks`
      }));
      setShowCreateListModal(true);
    }
  }, [searchParams]);

  // Create list
  const handleCreateList = async () => {
    if (!createListData.name.trim()) {
      toast.error('Please enter a list name');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/todolists`, createListData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLists(prev => [response.data, ...prev]);
      setSelectedListId(response.data.id);
      setShowCreateListModal(false);
      setCreateListData({
        name: '',
        sourceType: 'private',
        description: '',
        eventId: null,
        holdSlot: null
      });
      toast.success('List created');
      
      // Clear URL params
      navigate('/get-it-done', { replace: true });
    } catch (error) {
      console.error('Failed to create list:', error);
      toast.error(error.response?.data?.detail || 'Failed to create list');
    } finally {
      setSaving(false);
    }
  };

  // Create task
  const handleCreateTask = async () => {
    if (!createTaskData.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/todolists/${selectedListId}/tasks`,
        {
          ...createTaskData,
          dueAt: createTaskData.dueAt ? createTaskData.dueAt.toISOString() : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTasks(prev => [...prev, response.data]);
      setShowCreateTaskModal(false);
      setCreateTaskData({
        title: '',
        description: '',
        dueAt: null,
        priority: 'normal',
        assigneeId: null
      });
      toast.success('Task added');
      fetchTodayTasks(); // Refresh today view
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  // Toggle task completion
  const handleToggleTask = async (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      await axios.put(
        `${API}/todolists/${task.listId}/tasks/${task.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTasks(prev => prev.map(t => 
        t.id === task.id ? { ...t, status: newStatus } : t
      ));
      
      setTodayTasks(prev => prev.map(t => 
        t.id === task.id ? { ...t, status: newStatus } : t
      ));
      
      toast.success(newStatus === 'completed' ? 'Task completed!' : 'Task reopened');
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task');
    }
  };

  // Delete task
  const handleDeleteTask = async (task) => {
    try {
      await axios.delete(
        `${API}/todolists/${task.listId}/tasks/${task.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTasks(prev => prev.filter(t => t.id !== task.id));
      setTodayTasks(prev => prev.filter(t => t.id !== task.id));
      toast.success('Task deleted');
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('Failed to delete task');
    }
  };


  // Move task to another list
  const handleMoveTask = async (taskId, targetListId, targetListName) => {
    try {
      await axios.post(
        `${API}/todolists/${selectedListId}/tasks/${taskId}/move`,
        { targetListId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTasks(prev => prev.filter(t => t.id !== taskId));
      toast.success(`Task moved to "${targetListName}"`);
    } catch (error) {
      console.error('Failed to move task:', error);
      toast.error('Failed to move task');
    }
  };

  // Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = tasks.findIndex(t => t.id === active.id);
      const newIndex = tasks.findIndex(t => t.id === over.id);
      
      const newTasks = arrayMove(tasks, oldIndex, newIndex);
      setTasks(newTasks);
      
      // Save order to backend
      try {
        await axios.post(
          `${API}/todolists/${selectedListId}/tasks/reorder`,
          { taskIds: newTasks.map(t => t.id) },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to reorder tasks:', error);
      }
    }
  };

  // Delete list
  const handleDeleteList = async (listId) => {
    if (!window.confirm('Delete this list and all its tasks?')) return;
    
    try {
      await axios.delete(`${API}/todolists/${listId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLists(prev => prev.filter(l => l.id !== listId));
      if (selectedListId === listId) {
        setSelectedListId(null);
        setSelectedList(null);
        setTasks([]);
        setActiveView('today');
      }
      toast.success('List deleted');
    } catch (error) {
      console.error('Failed to delete list:', error);
      toast.error('Failed to delete list');
    }
  };

  // Group lists by type
  const holdLists = lists.filter(l => l.sourceType === 'hold');
  const eventLists = lists.filter(l => l.sourceType === 'event');
  const privateLists = lists.filter(l => l.sourceType === 'private');
  const circleLists = lists.filter(l => l.sourceType === 'circle');
  const groupLists = lists.filter(l => l.sourceType === 'group');

  // Group hold lists by event
  const holdListsByEvent = holdLists.reduce((acc, list) => {
    const key = list.eventId;
    if (!acc[key]) {
      acc[key] = {
        eventId: list.eventId,
        eventTitle: list.eventTitle,
        lists: []
      };
    }
    acc[key].lists.push(list);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GuestSignupBanner />
      {/* Header */}
      <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <ListTodo className="h-5 w-5 text-primary" />
              To-Do Lists
            </h1>
            <div className="flex items-center gap-1">
              <LanguageSelector variant="icon" />
              <Button
                size="sm"
                onClick={() => {
                if (isGuestMode) {
                  setShowGuestModal(true);
                  return;
                }
                // Fetch circles/groups for linking
                axios.get(`${API}/planner-circles`, { headers: getAuthHeaders() }).then(r => setCircles(r.data)).catch(() => {});
                axios.get(`${API}/groups`, { headers: getAuthHeaders() }).then(r => setGroups(r.data)).catch(() => {});
                setCreateListData({
                  name: '',
                  sourceType: 'private',
                  description: '',
                  eventId: null,
                  holdSlot: null,
                  circleId: null,
                  groupId: null
                });
                setShowCreateListModal(true);
              }}
              data-testid="create-list-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              New List
            </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto flex">
        {/* Sidebar */}
        <aside className="w-72 border-r min-h-[calc(100vh-57px)] p-4 space-y-6 bg-card/30">
          {/* Today View */}
          <div>
            <button
              onClick={() => {
                setActiveView('today');
                setSelectedListId(null);
                setSelectedList(null);
              }}
              className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                activeView === 'today' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'hover:bg-muted'
              }`}
              data-testid="today-view-btn"
            >
              <Sun className="h-5 w-5" />
              <span className="font-medium">Today</span>
              {todayTasks.length > 0 && (
                <Badge variant={activeView === 'today' ? 'secondary' : 'default'} className="ml-auto">
                  {todayTasks.filter(t => t.status === 'pending').length}
                </Badge>
              )}
            </button>
          </div>

          {/* My Hold To-Do Lists */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2 flex items-center gap-2">
              <Layers className="h-3 w-3" />
              Hold To-Do Lists
            </h3>
            {Object.keys(holdListsByEvent).length === 0 ? (
              <p className="text-xs text-muted-foreground px-2 py-2">No hold lists yet</p>
            ) : (
              <div className="space-y-2">
                {Object.values(holdListsByEvent).map(group => (
                  <div key={group.eventId} className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground px-2 truncate">
                      {group.eventTitle}
                    </p>
                    {group.lists.sort((a, b) => a.holdSlot - b.holdSlot).map(list => (
                      <button
                        key={list.id}
                        onClick={() => {
                          setSelectedListId(list.id);
                          setActiveView(null);
                        }}
                        className={`w-full flex items-center gap-2 p-2 pl-4 rounded-lg text-sm transition-colors ${
                          selectedListId === list.id 
                            ? 'bg-primary text-primary-foreground' 
                            : 'hover:bg-muted'
                        }`}
                        data-testid={`list-${list.id}`}
                      >
                        <Badge 
                          variant="outline" 
                          className={`text-[10px] px-1.5 ${getHoldSlotColor(list.holdSlot)}`}
                        >
                          {getHoldSlotLabel(list.holdSlot)}
                        </Badge>
                        <span className="truncate flex-1 text-left">{list.name}</span>
                        <span className="text-xs opacity-70">
                          {list.completedCount}/{list.taskCount}
                        </span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Event Lists */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2 flex items-center gap-2">
              <Users className="h-3 w-3" />
              Event Lists
            </h3>
            {eventLists.length === 0 ? (
              <p className="text-xs text-muted-foreground px-2 py-2">No event lists yet</p>
            ) : (
              <div className="space-y-1">
                {eventLists.map(list => (
                  <button
                    key={list.id}
                    onClick={() => {
                      setSelectedListId(list.id);
                      setActiveView(null);
                    }}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg text-sm transition-colors ${
                      selectedListId === list.id 
                        ? 'bg-primary text-primary-foreground' 
                        : 'hover:bg-muted'
                    }`}
                    data-testid={`list-${list.id}`}
                  >
                    <Users className="h-4 w-4 text-blue-500" />
                    <span className="truncate flex-1 text-left">{list.name}</span>
                    <span className="text-xs opacity-70">
                      {list.completedCount}/{list.taskCount}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Private Lists */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2 flex items-center gap-2">
              <Lock className="h-3 w-3" />
              Private Lists
            </h3>
            {privateLists.length === 0 ? (
              <p className="text-xs text-muted-foreground px-2 py-2">No private lists yet</p>
            ) : (
              <div className="space-y-1">
                {privateLists.map(list => (
                  <button
                    key={list.id}
                    onClick={() => {
                      setSelectedListId(list.id);
                      setActiveView(null);
                    }}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg text-sm transition-colors ${
                      selectedListId === list.id 
                        ? 'bg-primary text-primary-foreground' 
                        : 'hover:bg-muted'
                    }`}
                    data-testid={`list-${list.id}`}
                  >
                    <Lock className="h-4 w-4 text-violet-500" />
                    <span className="truncate flex-1 text-left">{list.name}</span>
                    <span className="text-xs opacity-70">
                      {list.completedCount}/{list.taskCount}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Circle Lists */}
          {circleLists.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2 flex items-center gap-2">
                <Users2 className="h-3 w-3" />
                Circle Lists
              </h3>
              <div className="space-y-1">
                {circleLists.map(list => (
                  <button
                    key={list.id}
                    onClick={() => { setSelectedListId(list.id); setActiveView(null); }}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg text-sm transition-colors ${
                      selectedListId === list.id ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                    }`}
                    data-testid={`list-${list.id}`}
                  >
                    <Users2 className="h-4 w-4 text-cyan-500" />
                    <div className="flex-1 min-w-0 text-left">
                      <span className="truncate block">{list.name}</span>
                      {list.circleName && <span className="text-xs opacity-60 truncate block">{list.circleName}</span>}
                    </div>
                    <span className="text-xs opacity-70">{list.completedCount}/{list.taskCount}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Group Lists */}
          {groupLists.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2 flex items-center gap-2">
                <Users className="h-3 w-3" />
                Group Lists
              </h3>
              <div className="space-y-1">
                {groupLists.map(list => (
                  <button
                    key={list.id}
                    onClick={() => { setSelectedListId(list.id); setActiveView(null); }}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg text-sm transition-colors ${
                      selectedListId === list.id ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                    }`}
                    data-testid={`list-${list.id}`}
                  >
                    <Users className="h-4 w-4 text-green-500" />
                    <div className="flex-1 min-w-0 text-left">
                      <span className="truncate block">{list.name}</span>
                      {list.groupName && <span className="text-xs opacity-60 truncate block">{list.groupName}</span>}
                    </div>
                    <span className="text-xs opacity-70">{list.completedCount}/{list.taskCount}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quick Links */}
          <div className="pt-2 border-t">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2">
              Quick Links
            </h3>
            <div className="space-y-1">
              <button
                onClick={() => navigate('/planner-circles')}
                className="w-full flex items-center gap-2 p-2 rounded-lg text-sm hover:bg-muted transition-colors"
                data-testid="sidebar-circles-link"
              >
                <Users2 className="h-4 w-4 text-cyan-500" />
                <span className="truncate flex-1 text-left">Event Planner Circles</span>
                <ChevronRight className="h-3 w-3 text-muted-foreground" />
              </button>
              <button
                onClick={() => navigate('/groups')}
                className="w-full flex items-center gap-2 p-2 rounded-lg text-sm hover:bg-muted transition-colors"
                data-testid="sidebar-groups-link"
              >
                <Layers className="h-4 w-4 text-teal-500" />
                <span className="truncate flex-1 text-left">Invite Groups</span>
                <ChevronRight className="h-3 w-3 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Documents Section */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Documents
              </h3>
              <span className="text-[10px] text-muted-foreground">{docInfo.totalCount}/{docInfo.uploadLimit}</span>
            </div>
            <input
              ref={docInputRef}
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={handleDocUpload}
              data-testid="doc-upload-input"
            />
            <Button
              variant="outline"
              size="sm"
              className="w-full text-xs mb-2 border-dashed"
              onClick={() => docInputRef.current?.click()}
              disabled={uploadingDoc || docInfo.remainingSlots <= 0}
              data-testid="upload-doc-btn"
            >
              {uploadingDoc ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
              {docInfo.remainingSlots > 0 ? 'Upload PDF' : 'Limit reached'}
            </Button>
            {documents.length > 0 ? (
              <div className="space-y-1">
                {documents.map(doc => (
                  <div key={doc.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-colors group text-sm" data-testid={`doc-${doc.id}`}>
                    <FileText className="h-4 w-4 text-red-500 flex-shrink-0" />
                    <span className="truncate flex-1 text-left text-xs">{doc.originalFilename}</span>
                    <a
                      href={`${API}/documents/${doc.id}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`download-doc-${doc.id}`}
                    >
                      <Download className="h-3 w-3 text-muted-foreground hover:text-primary" />
                    </a>
                    <button
                      onClick={() => handleDocDelete(doc.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-doc-${doc.id}`}
                    >
                      <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-muted-foreground text-center py-2">No documents yet</p>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {/* Today View */}
          {activeView === 'today' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Sun className="h-6 w-6 text-amber-500" />
                  Today
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  {format(new Date(), 'EEEE, MMMM d, yyyy')}
                </p>
              </div>

              {todayTasks.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-12 text-center">
                    <Sun className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-muted-foreground">No tasks due today</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Tasks with today&apos;s due date will appear here
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {todayTasks.map(task => (
                    <div
                      key={task.id}
                      className={`p-4 rounded-lg border bg-card hover:shadow-sm transition-all group ${
                        task.status === 'completed' ? 'opacity-60' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() => handleToggleTask(task)}
                          className="mt-0.5"
                          data-testid={`toggle-today-task-${task.id}`}
                        >
                          {task.status === 'completed' ? (
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                          ) : (
                            <Circle className="h-5 w-5 text-muted-foreground hover:text-primary" />
                          )}
                        </button>
                        
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                            {task.title}
                          </p>
                          
                          {/* Source indicator */}
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {task.sourceType === 'hold' && (
                              <Badge variant="outline" className={`text-xs ${getHoldSlotColor(task.holdSlot)}`}>
                                {getHoldSlotLabel(task.holdSlot)} Hold
                              </Badge>
                            )}
                            {task.sourceType === 'event' && (
                              <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                <Users className="h-3 w-3 mr-1" />
                                Shared
                              </Badge>
                            )}
                            {task.sourceType === 'private' && (
                              <Badge variant="outline" className="text-xs bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                                <Lock className="h-3 w-3 mr-1" />
                                Private
                              </Badge>
                            )}
                            
                            {task.listName && (
                              <button
                                onClick={() => setSelectedListId(task.listId)}
                                className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
                              >
                                {task.listName}
                                <ArrowUpRight className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Selected List View */}
          {selectedList && (
            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    {selectedList.sourceType === 'hold' && (
                      <Badge className={getHoldSlotColor(selectedList.holdSlot)}>
                        {getHoldSlotLabel(selectedList.holdSlot)} Hold
                      </Badge>
                    )}
                    {selectedList.sourceType === 'event' && (
                      <Badge className="bg-blue-100 text-blue-700">
                        <Users className="h-3 w-3 mr-1" />
                        Shared
                      </Badge>
                    )}
                    {selectedList.sourceType === 'private' && (
                      <Badge className="bg-violet-100 text-violet-700">
                        <Lock className="h-3 w-3 mr-1" />
                        Private
                      </Badge>
                    )}
                    {selectedList.sourceType === 'circle' && (
                      <Badge className="bg-cyan-100 text-cyan-700">
                        <Users2 className="h-3 w-3 mr-1" />
                        Circle
                      </Badge>
                    )}
                    {selectedList.sourceType === 'group' && (
                      <Badge className="bg-green-100 text-green-700">
                        <Users className="h-3 w-3 mr-1" />
                        Group
                      </Badge>
                    )}
                  </div>
                  <h2 className="text-2xl font-bold mt-2">{selectedList.name}</h2>
                  {selectedList.eventTitle && (
                    <button
                      onClick={() => navigate(`/events/${selectedList.eventId}`)}
                      className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1 mt-1"
                    >
                      {selectedList.eventTitle}
                      <ArrowUpRight className="h-3 w-3" />
                    </button>
                  )}
                  {selectedList.circleName && (
                    <button
                      onClick={() => navigate(`/planner-circles/${selectedList.circleId}`)}
                      className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1 mt-1"
                      data-testid="linked-circle-link"
                    >
                      <Users2 className="h-3 w-3" />
                      {selectedList.circleName}
                      <ArrowUpRight className="h-3 w-3" />
                    </button>
                  )}
                  {selectedList.groupName && (
                    <button
                      onClick={() => navigate(`/groups/${selectedList.groupId}`)}
                      className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1 mt-1"
                      data-testid="linked-group-link"
                    >
                      <Users className="h-3 w-3" />
                      {selectedList.groupName}
                      <ArrowUpRight className="h-3 w-3" />
                    </button>
                  )}
                  {selectedList.description && (
                    <p className="text-sm text-muted-foreground mt-1">{selectedList.description}</p>
                  )}
                </div>
                
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Edit List */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingListName(selectedList.name);
                      setEditingListDesc(selectedList.description || '');
                      setShowEditListModal(true);
                    }}
                    data-testid="edit-list-btn"
                  >
                    <Pencil className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  {/* Copy List */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const lines = [`${selectedList.name}`, '—'.repeat(30)];
                      tasks.forEach(t => {
                        const check = t.status === 'completed' ? '[x]' : '[ ]';
                        let line = `${check} ${t.title}`;
                        if (t.dueAt) line += ` (due: ${t.dueAt.split('T')[0]})`;
                        if (t.assigneeName) line += ` → ${t.assigneeName}`;
                        lines.push(line);
                      });
                      if (selectedList.description) lines.splice(1, 0, selectedList.description);
                      lines.push('', 'Shared from PlanBlox');
                      navigator.clipboard.writeText(lines.join('\n'));
                      toast.success('List copied to clipboard');
                    }}
                    data-testid="copy-list-btn"
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    Copy
                  </Button>
                  {/* Duplicate List */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const res = await axios.post(`${API}/todolists`, {
                          name: `${selectedList.name} (Copy)`,
                          description: selectedList.description,
                          sourceType: selectedList.sourceType,
                        }, { headers: { Authorization: `Bearer ${token}` } });
                        // Copy all tasks to new list
                        for (const t of tasks) {
                          await axios.post(`${API}/todolists/${res.data.id}/tasks`, {
                            title: t.title,
                            description: t.description,
                            priority: t.priority,
                            dueAt: t.dueAt,
                          }, { headers: { Authorization: `Bearer ${token}` } });
                        }
                        toast.success('List duplicated');
                        fetchLists();
                      } catch {
                        toast.error('Failed to duplicate list');
                      }
                    }}
                    data-testid="duplicate-list-btn"
                  >
                    <Layers className="h-4 w-4 mr-1" />
                    Duplicate
                  </Button>
                  {/* Share */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const tasks_text = tasks.map(t => `${t.status === 'completed' ? '[x]' : '[ ]'} ${t.title}`).join('\n');
                      const shareText = `${selectedList.name}\n${'—'.repeat(20)}\n${tasks_text || 'No tasks yet'}\n\nShared from PlanBlox`;
                      if (navigator.share) {
                        navigator.share({ title: selectedList.name, text: shareText });
                      } else {
                        navigator.clipboard.writeText(shareText);
                        toast.success('List copied to clipboard');
                      }
                    }}
                    data-testid="share-list-btn"
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>
                  {/* Delete */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteList(selectedList.id)}
                    className="text-destructive hover:text-destructive"
                    data-testid="delete-list-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Add task button */}
              <Button
                onClick={() => setShowCreateTaskModal(true)}
                className="w-full"
                variant="outline"
                data-testid="add-task-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Task
              </Button>

              {/* Task Search */}
              {tasks.length > 3 && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search tasks..."
                    value={taskSearchQuery}
                    onChange={e => setTaskSearchQuery(e.target.value)}
                    className="pl-9 h-9"
                    data-testid="task-search-input"
                  />
                  {taskSearchQuery && (
                    <button
                      onClick={() => setTaskSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              )}

              {/* Tasks list */}
              {tasks.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="py-12 text-center">
                    <ListTodo className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-muted-foreground">No tasks yet</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Add your first task to get started
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={tasks.map(t => t.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {tasks.filter(t => !taskSearchQuery || t.title.toLowerCase().includes(taskSearchQuery.toLowerCase()) || (t.description || '').toLowerCase().includes(taskSearchQuery.toLowerCase())).map(task => (
                        <SortableTask
                          key={task.id}
                          task={task}
                          onToggle={handleToggleTask}
                          onDelete={handleDeleteTask}
                          attachmentCounts={attachmentCounts}
                          onOpenAttachments={handleOpenAttachments}
                          allLists={lists}
                          currentListId={selectedListId}
                          onMove={handleMoveTask}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}

              {/* Progress */}
              {tasks.length > 0 && (
                <div className="text-sm text-muted-foreground text-center">
                  {tasks.filter(t => t.status === 'completed').length} of {tasks.length} tasks completed
                </div>
              )}
            </div>
          )}

          {/* No selection state */}
          {!activeView && !selectedList && (
            <div className="flex items-center justify-center h-full min-h-[400px]">
              <div className="text-center">
                <ListTodo className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">Select a list or view to get started</p>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Create List Modal */}
      <Dialog open={showCreateListModal} onOpenChange={setShowCreateListModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New List</DialogTitle>
            <DialogDescription>
              Create a to-do list to track your tasks
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>List Name</Label>
              <Input
                placeholder="e.g., Venue Tasks, Catering Checklist"
                value={createListData.name}
                onChange={(e) => setCreateListData(prev => ({ ...prev, name: e.target.value }))}
                data-testid="create-list-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>List Type</Label>
              <Select
                value={createListData.sourceType}
                onValueChange={(value) => setCreateListData(prev => ({ 
                  ...prev, 
                  sourceType: value,
                  eventId: value === 'private' || value === 'circle' || value === 'group' ? null : prev.eventId,
                  holdSlot: value === 'private' || value === 'circle' || value === 'group' ? null : prev.holdSlot,
                  circleId: value === 'circle' ? prev.circleId : null,
                  groupId: value === 'group' ? prev.groupId : null
                }))}
              >
                <SelectTrigger data-testid="list-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">
                    <div className="flex items-center gap-2">
                      <Lock className="h-4 w-4 text-violet-500" />
                      Private List
                    </div>
                  </SelectItem>
                  <SelectItem value="hold" disabled={!createListData.eventId}>
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4 text-amber-500" />
                      Hold List
                    </div>
                  </SelectItem>
                  <SelectItem value="event">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-blue-500" />
                      Event List (Collaborative)
                    </div>
                  </SelectItem>
                  {circles.length > 0 && (
                    <SelectItem value="circle">
                      <div className="flex items-center gap-2">
                        <Users2 className="h-4 w-4 text-cyan-500" />
                        Circle List
                      </div>
                    </SelectItem>
                  )}
                  {groups.length > 0 && (
                    <SelectItem value="group">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-green-500" />
                        Group List
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Circle Picker */}
            {createListData.sourceType === 'circle' && (
              <div className="space-y-2">
                <Label>Select Circle</Label>
                <Select
                  value={createListData.circleId || ''}
                  onValueChange={(value) => setCreateListData(prev => ({ ...prev, circleId: value }))}
                >
                  <SelectTrigger data-testid="circle-select">
                    <SelectValue placeholder="Choose a circle..." />
                  </SelectTrigger>
                  <SelectContent>
                    {circles.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Group Picker */}
            {createListData.sourceType === 'group' && (
              <div className="space-y-2">
                <Label>Select Group</Label>
                <Select
                  value={createListData.groupId || ''}
                  onValueChange={(value) => setCreateListData(prev => ({ ...prev, groupId: value }))}
                >
                  <SelectTrigger data-testid="group-select">
                    <SelectValue placeholder="Choose a group..." />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {createListData.sourceType === 'hold' && createListData.holdSlot && (
              <div className="p-3 rounded-lg bg-muted">
                <p className="text-sm">
                  <span className="font-medium">Creating list for:</span>{' '}
                  <Badge className={getHoldSlotColor(createListData.holdSlot)}>
                    {getHoldSlotLabel(createListData.holdSlot)} Hold
                  </Badge>
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                placeholder="What's this list for?"
                value={createListData.description}
                onChange={(e) => setCreateListData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                data-testid="create-list-description-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateListModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateList} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create List
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Task Modal */}
      <Dialog open={showCreateTaskModal} onOpenChange={setShowCreateTaskModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
            <DialogDescription>
              Add a new task to {selectedList?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Task Title</Label>
              <Input
                placeholder="What needs to be done?"
                value={createTaskData.title}
                onChange={(e) => setCreateTaskData(prev => ({ ...prev, title: e.target.value }))}
                data-testid="create-task-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                placeholder="Add more details..."
                value={createTaskData.description}
                onChange={(e) => setCreateTaskData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                data-testid="create-task-description-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Due Date (optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {createTaskData.dueAt ? format(createTaskData.dueAt, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={createTaskData.dueAt}
                      onSelect={(date) => setCreateTaskData(prev => ({ ...prev, dueAt: date }))}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={createTaskData.priority}
                  onValueChange={(value) => setCreateTaskData(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Assignment - show for shared/event lists or if user has connections */}
            {(selectedList?.sourceType === 'event' || connections.length > 0) && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Assign To (optional)
                </Label>
                <Select
                  value={createTaskData.assigneeId || 'unassigned'}
                  onValueChange={(value) => setCreateTaskData(prev => ({ 
                    ...prev, 
                    assigneeId: value === 'unassigned' ? null : value 
                  }))}
                >
                  <SelectTrigger data-testid="task-assignee-select">
                    <SelectValue placeholder="Select person" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">
                      <span className="text-muted-foreground">Unassigned</span>
                    </SelectItem>
                    <SelectItem value={user?.id}>
                      <div className="flex items-center gap-2">
                        <span>Myself</span>
                        <Badge variant="outline" className="text-xs">You</Badge>
                      </div>
                    </SelectItem>
                    {connections.map(conn => (
                      <SelectItem key={conn.connectedUserId} value={conn.connectedUserId}>
                        <div className="flex items-center gap-2">
                          <img 
                            src={conn.connectedUserPhoto || `https://api.dicebear.com/7.x/initials/svg?seed=${conn.connectedUserName}`}
                            alt=""
                            className="w-5 h-5 rounded-full"
                          />
                          <span>{conn.connectedUserName}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTaskModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTask} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit List Modal */}
      <Dialog open={showEditListModal} onOpenChange={setShowEditListModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit List</DialogTitle>
            <DialogDescription>Update the name and description of this to-do list.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input
                value={editingListName}
                onChange={(e) => setEditingListName(e.target.value)}
                data-testid="edit-list-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input
                value={editingListDesc}
                onChange={(e) => setEditingListDesc(e.target.value)}
                placeholder="Optional description"
                data-testid="edit-list-desc-input"
              />
            </div>
            <Button
              className="w-full"
              onClick={async () => {
                if (!selectedList || !editingListName.trim()) return;
                try {
                  await axios.put(`${API}/todolists/${selectedList.id}`, {
                    name: editingListName.trim(),
                    description: editingListDesc.trim(),
                  }, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success('List updated');
                  setShowEditListModal(false);
                  fetchLists();
                } catch {
                  toast.error('Failed to update list');
                }
              }}
              data-testid="save-list-edit-btn"
            >
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Attachments Modal */}
      <Dialog open={showAttachmentsModal} onOpenChange={(open) => {
        setShowAttachmentsModal(open);
        if (!open) {
          setAttachmentTask(null);
          setAttachments([]);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-4 w-4" />
              Attachments
            </DialogTitle>
            <DialogDescription>
              {attachmentTask?.title} — PDF files only, max 10 MB
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Upload button */}
            <div>
              <input
                type="file"
                accept=".pdf,application/pdf"
                id="pdf-upload-input"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUploadAttachment(file);
                  e.target.value = '';
                }}
                data-testid="pdf-upload-input"
              />
              <Button
                variant="outline"
                className="w-full"
                disabled={uploadingFile}
                onClick={() => document.getElementById('pdf-upload-input').click()}
                data-testid="upload-pdf-btn"
              >
                {uploadingFile ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                {uploadingFile ? 'Uploading...' : 'Upload PDF'}
              </Button>
            </div>

            {/* Attachments list */}
            {loadingAttachments ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : attachments.length === 0 ? (
              <div className="text-center py-6">
                <FileText className="h-10 w-10 text-muted-foreground/40 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No attachments yet</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {attachments.map(att => (
                  <div
                    key={att.id}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                    data-testid={`attachment-${att.id}`}
                  >
                    <FileText className="h-8 w-8 text-red-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{att.originalFilename}</p>
                      <p className="text-xs text-muted-foreground">
                        {att.size < 1024 ? `${att.size} B` :
                         att.size < 1024 * 1024 ? `${(att.size / 1024).toFixed(1)} KB` :
                         `${(att.size / (1024 * 1024)).toFixed(1)} MB`}
                        {att.uploadedByName ? ` — ${att.uploadedByName}` : ''}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 flex-shrink-0"
                      onClick={() => handleDownloadAttachment(att)}
                      title="Download"
                      data-testid={`download-attachment-${att.id}`}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 flex-shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDeleteAttachment(att)}
                      title="Delete"
                      data-testid={`delete-attachment-${att.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Guest Action Modal */}
      <GuestActionModal open={showGuestModal} onOpenChange={setShowGuestModal} />
    </div>
  );
};

export default TodoLists;
